import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { ifcRootGuid } from '@constants'
import produce from 'immer'
import { findIndex } from 'lodash-es'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getProjectFiles } from '@queries'
import { postFileMetadata } from '@mutations'
import type {
  GlbFileAdditionalData,
  IfcModelMetadata,
} from '../../components/Controls/IFCUploadWidget/types/ifcMetadata'
import useIfcFiles from './useIfcFiles'

export type MetadataUpdater = (current: IfcModelMetadata | null) => IfcModelMetadata | null

const useUpdateIfcModelMetadata = (activeIfcFileGuid: string | null) => {
  const { projectId = '' } = useParams()
  const glbFiles = useIfcFiles()
  const queryClient = useQueryClient()
  const queryKey = getProjectFiles.getKey(projectId, ifcRootGuid)

  const activeGlbFile = useMemo(
    () =>
      activeIfcFileGuid
        ? glbFiles.find((file: FileItem) => file.guid === activeIfcFileGuid) ?? null
        : null,
    [activeIfcFileGuid, glbFiles],
  )

  const { mutate, mutateAsync, isLoading } = useMutation({
    mutationFn: async (nextModelMetadata: IfcModelMetadata) => {
      if (!projectId || !activeGlbFile) return activeGlbFile as FileItem

      const additionalData = (activeGlbFile.additional_data ?? {}) as GlbFileAdditionalData
      return postFileMetadata.request(projectId, ifcRootGuid, {
        item_guid: activeGlbFile.guid,
        name: activeGlbFile.name ?? '',
        ...additionalData,
        model_metadata: nextModelMetadata,
      })
    },
    onMutate: async (nextModelMetadata: IfcModelMetadata) => {
      await queryClient.cancelQueries(queryKey)

      const previousFiles = queryClient.getQueryData<FilesResult>(queryKey)

      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft || !activeGlbFile) return

          const index = findIndex(draft.files, { guid: activeGlbFile.guid })
          if (index === -1) return

          const additionalData = (draft.files[index].additional_data ?? {}) as GlbFileAdditionalData
          draft.files[index].additional_data = {
            ...additionalData,
            model_metadata: nextModelMetadata,
          }
        }),
      )

      return { previousFiles }
    },
    onError: async () => {
      await queryClient.invalidateQueries(queryKey)
    },
    mutationKey: ['updateIfcModelMetadata', projectId, ifcRootGuid, activeIfcFileGuid],
    meta: {
      title: 'IFC-Metadaten speichern',
      description: 'Speichert die IFC-Zuweisungen (Typ, Stockwerk, Filter).',
    },
  })

  const currentModelMetadata = useMemo(
    () =>
      ((activeGlbFile?.additional_data as GlbFileAdditionalData | undefined)?.model_metadata ??
        null) as IfcModelMetadata | null,
    [activeGlbFile],
  )

  // The IFC metadata is read asynchronously in a background job after upload and only then written
  // to the file's additional_data.model_metadata. Until that field exists, updates are disabled so
  // we don't accidentally overwrite the field while the metadata is still being computed.
  const hasIfcModelMetadata = currentModelMetadata != null

  const buildNextModelMetadata = useCallback(
    (updater: MetadataUpdater) => updater(currentModelMetadata),
    [currentModelMetadata],
  )

  const updateIfcModelMetadata = useCallback(
    (updater: MetadataUpdater) => {
      if (!hasIfcModelMetadata) return
      const nextModelMetadata = buildNextModelMetadata(updater)
      if (!nextModelMetadata) return
      mutate(nextModelMetadata)
    },
    [hasIfcModelMetadata, buildNextModelMetadata, mutate],
  )

  const updateIfcModelMetadataAsync = useCallback(
    async (updater: MetadataUpdater) => {
      if (!hasIfcModelMetadata) return
      const nextModelMetadata = buildNextModelMetadata(updater)
      if (!nextModelMetadata) return
      return mutateAsync(nextModelMetadata)
    },
    [hasIfcModelMetadata, buildNextModelMetadata, mutateAsync],
  )

  return {
    activeGlbFile,
    hasIfcModelMetadata,
    isUpdatingIfcModelMetadata: isLoading,
    updateIfcModelMetadata,
    updateIfcModelMetadataAsync,
  }
}

export default useUpdateIfcModelMetadata
